/* global BigInt */
import { ethers } from 'ethers';
import {
    COINGECKO_URL,
    merkletree,
    merkletreeprivatekey,
    MINTPRICE_USD,
    mintpriceinsol,
    RPC_ENDPOINT_L,
} from '../../constants/apiPaths';
import { createSignerFromKeypair, publicKey } from '@metaplex-foundation/umi';
import { mintToCollectionV1 } from '@metaplex-foundation/mpl-bubblegum';
import { createUmi } from '@metaplex-foundation/umi-bundle-defaults';
import { walletAdapterIdentity } from '@metaplex-foundation/umi-signer-wallet-adapters';

import {
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    SystemProgram,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { toWeb3JsInstruction } from '@metaplex-foundation/umi-web3js-adapters';
import { getRpcUrl } from './getRPCURL';
import { getMetaMaskProvider } from '../../blockchainInteraction/finaliseGrading';
import {
    createAssociatedTokenAccountInstruction,
    createTransferInstruction,
    getAccount,
    getAssociatedTokenAddress,
    getAssociatedTokenAddressSync,
} from '@solana/spl-token';
// const walletAdapterIdentity = {};
/*
  transfer SOL equal to $5 to the treasury
  mint one NFT to user's wallet
  requirements: treasury publickey, tree creator keypair, user keypair,
*/

// const mintToCollectionV1 = () => {};

const ERC721_ABI = [
    'function isTokenWhitelisted(address tokenAddress) public view returns (bool)',
    'function getTokenAmount(address tokenAddress) public view returns (uint256)',
    'function mintWithToken(address tokenAddress) public',
    'function mintPrice() public view returns (uint256)',
    'function mint() public payable',
];

export const ERC20_ABI = [
    'function approve(address spender, uint256 amount) external returns (bool)',
    'function allowance(address owner, address spender) external view returns (uint256)',
    'function balanceOf(address account) external view returns (uint256)',
    'function totalSupply() external view returns (uint256)',
];

export const getMintPriceForEVMInStableCoin = async (contractAddress, chainId, erc20Address) => {
    const provider = new ethers.JsonRpcProvider(getRpcUrl(chainId));

    const nftContract = new ethers.Contract(contractAddress, ERC721_ABI, provider);

    try {
        const whitelisted = await nftContract.isTokenWhitelisted(erc20Address);

        if (!whitelisted) {
            throw new Error('Token not whitelisted for minting');
        }

        const mintPrice = await nftContract.getTokenAmount(erc20Address);

        return mintPrice;
    } catch (error) {
        throw error;
    }
};

export const getWhitelistedTokenAddresses = async (chainId, erc721Address) => {
    try {
        const provider = new ethers.JsonRpcProvider(getRpcUrl(chainId));
        // const wallet = new ethers.Wallet(process.env.PRIVATE_KEY, provider)
        const erc721Contract = new ethers.Contract(
            erc721Address,
            ['function getWhitelistedTokens() view returns (address[])'],
            provider
        );
        const tokenAddresses = await erc721Contract.getWhitelistedTokens();
        return tokenAddresses;
    } catch (error) {
        throw error;
    }
};

export const getTokenBalance = async (chainId, tokenAddress, walletAddress) => {
    const rpcUrl = getRpcUrl(chainId);

    if (!rpcUrl) {
        return 0;
    }

    const provider = new ethers.JsonRpcProvider(rpcUrl);
    // const wallet = new ethers.Wallet(process.env.PRIVATE_KEY, provider)
    const erc20Contract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const senderBalance = await erc20Contract.balanceOf(walletAddress);
    return senderBalance;
};

export const getMintPriceForEVM = async (contractAddress, chainId) => {
    const provider = new ethers.JsonRpcProvider(getRpcUrl(chainId));

    const contractABI = ['function mintPrice() public view returns (uint256)'];

    const contract = new ethers.Contract(contractAddress, contractABI, provider);

    try {
        const rawPrice = await contract.mintPrice();

        const decimals = 18;

        const mintPrice = Number(rawPrice) / Math.pow(10, decimals);

        return mintPrice;
    } catch (error) {
        throw error;
    }
};

export const getMintTransaction = async (
    metadataUri,
    nftName,
    collectionMintString,
    treasuryAddress,
    connection,
    wallet // useWallet() passed here
) => {
    const phantomAddressPublicKey = wallet.publicKey;
    const collectionMint = publicKey(collectionMintString);
    const treasury = new PublicKey(treasuryAddress);
    const merkleTree = publicKey(merkletree);
    const mintPriceInSol = Number(mintpriceinsol);

    const umi = createUmi(RPC_ENDPOINT_L);
    umi.use(walletAdapterIdentity(wallet));

    const keypair1 = Keypair.fromSecretKey(new Uint8Array(merkletreeprivatekey?.split(',')?.map(Number)));

    const treeCreatorKeypair = umi.eddsa.createKeypairFromSecretKey(keypair1.secretKey);
    const treeCreatorKeypairSigner = createSignerFromKeypair(umi, treeCreatorKeypair);

    const transferInstruction = SystemProgram.transfer({
        fromPubkey: phantomAddressPublicKey,
        toPubkey: treasury,
        lamports: mintPriceInSol * LAMPORTS_PER_SOL,
    });

    const mintInstructions = mintToCollectionV1(umi, {
        leafOwner: phantomAddressPublicKey,
        merkleTree,
        collectionMint,
        metadata: {
            name: nftName,
            uri: metadataUri,
            sellerFeeBasisPoints: 0,
            symbol: 'PILL',
            collection: { key: collectionMint, verified: false },
            creators: [
                {
                    address: treeCreatorKeypair.publicKey,
                    verified: false,
                    share: 100,
                },
            ],
        },
        treeCreatorOrDelegate: treeCreatorKeypairSigner,
        collectionAuthority: treeCreatorKeypairSigner,
    })
        .getInstructions()
        .map(toWeb3JsInstruction);

    const blockhash = (await connection.getLatestBlockhash()).blockhash;

    const message = new TransactionMessage({
        payerKey: wallet.publicKey,
        recentBlockhash: blockhash,
        instructions: [transferInstruction, ...mintInstructions],
    }).compileToV0Message();

    const transaction = new VersionedTransaction(message);

    transaction.sign([Keypair.fromSecretKey(treeCreatorKeypair.secretKey)]);

    return transaction;
};

export const mintNFT = async (contractAddress, chainId) => {
    // Configuration
    //const contractAddress = '0x064B8F36ED46592a61b463BE3DA635Aeb5B2D41F';
    //   const contractAddress = "0x292ED3701f74018117fe8cc29FE569259139BcE1"

    const metaMaskProvider = getMetaMaskProvider();

    try {
        // Request user accounts from MetaMask
        const accounts = await metaMaskProvider.request({
            method: 'eth_requestAccounts',
        });
        const from = accounts[0];

        const web3Provider = new ethers.BrowserProvider(metaMaskProvider);
        const signer = await web3Provider.getSigner();

        // Create contract instance with the signer
        const nftContract = new ethers.Contract(contractAddress, ERC721_ABI, signer);

        // Get mint price from contract
        const mintPrice = await nftContract.mintPrice();

        // Encode the mint function call
        const mintData = new ethers.Interface(['function mint() public payable']).encodeFunctionData('mint', []);

        const transactionParams = {
            to: contractAddress, // Contract address
            from: from, // Connected account
            data: mintData, // Encoded function call
            value: '0x' + mintPrice.toString(16), // Payment required for minting
        };

        // Send the transaction using MetaMask's request method
        const result = await metaMaskProvider.request({
            method: 'eth_sendTransaction',
            params: [transactionParams],
        });

        return { success: true, result };
    } catch (error) {
        return { success: false, code: error?.message };
    }
};

export const mintNftWithToken = async (erc20Address, nftAddress, chainId) => {
    try {
        const metaMaskProvider = getMetaMaskProvider();

        const accounts = await metaMaskProvider.request({
            method: 'eth_requestAccounts',
        });

        const from = accounts[0];

        const web3Provider = new ethers.BrowserProvider(metaMaskProvider);

        const signer = await web3Provider.getSigner();

        const nftContract = new ethers.Contract(nftAddress, ERC721_ABI, signer);
        const erc20Contract = new ethers.Contract(erc20Address, ERC20_ABI, signer);

        const whitelisted = await nftContract.isTokenWhitelisted(erc20Address);

        if (!whitelisted) {
            throw new Error('Token not whitelisted for minting');
        }

        const mintPrice = await nftContract.getTokenAmount(erc20Address);

        const approveData = new ethers.Interface([
            'function approve(address spender, uint256 amount) external returns (bool)',
        ]).encodeFunctionData('approve', [nftAddress, mintPrice]);

        const transactionParams = {
            to: erc20Address, // Contract address
            from: from, // Connected account
            data: approveData, // Encoded function call
        };

        // Send the transaction using MetaMask's request method
        const result = await metaMaskProvider.request({
            method: 'eth_sendTransaction',
            params: [transactionParams],
        });

        // const approved = await erc20Contract.approve(nftAddress, mintPrice)
        // if (!approved) {
        //   throw new Error("Failed to approve transferring mint price")
        // }
        const [waitTime, maxRetries] = [1000, 5];
        let allowance;
        for (let i = 0; i < maxRetries; ++i) {
            allowance = await erc20Contract.allowance(from, nftAddress);
            if (allowance < mintPrice) {
                await new Promise((res) => setTimeout(res, waitTime));
            } else {
                break;
            }
        }

        if (mintPrice > allowance) {
            throw new Error('Could not allow approving transfer of mint price');
        }

        const mintData = new ethers.Interface([
            'function mintWithToken(address tokenAddress) public',
        ]).encodeFunctionData('mintWithToken', [erc20Address]);

        const transactionParams2 = {
            to: nftAddress, // Contract address
            from: from, // Connected account
            data: mintData, // Encoded function call
        };

        // Send the transaction using MetaMask's request method
        const result2 = await metaMaskProvider.request({
            method: 'eth_sendTransaction',
            params: [transactionParams2],
        });

        return { success: true, result: result2 };
    } catch (error) {
        console.error(error, error?.code);
        return { success: false, code: error?.message };
    }
};

export const getMintTransactionWithStableCoin = async (
    metadataUri,
    nftName,
    collectionMintString,
    treasuryAddress,
    connection,
    wallet,
    tokenAddress,
    decimals
) => {
    const phantomAddressPublicKey = wallet.publicKey;
    const collectionMint = publicKey(collectionMintString);
    const treasury = new PublicKey(treasuryAddress);
    const merkleTree = publicKey(merkletree);

    const umi = createUmi(RPC_ENDPOINT_L);

    umi.use(walletAdapterIdentity(wallet));

    const keypair1 = Keypair.fromSecretKey(new Uint8Array(merkletreeprivatekey?.split(',')?.map(Number)));

    const treeCreatorKeypair = umi.eddsa.createKeypairFromSecretKey(keypair1.secretKey);
    const treeCreatorKeypairSigner = createSignerFromKeypair(umi, treeCreatorKeypair);

    const fromPublicKey = phantomAddressPublicKey;
    const toPublicKey = treasury;
    const mintPublicKey = new PublicKey(tokenAddress);

    const rawAmount = BigInt(Math.floor(MINTPRICE_USD * Math.pow(10, decimals)));

    // Get associated token addresses
    const fromTokenAccount = getAssociatedTokenAddressSync(mintPublicKey, fromPublicKey);

    const toTokenAccount = getAssociatedTokenAddressSync(mintPublicKey, toPublicKey, true);

    const transferInstructions = [];

    // Check if receiver's token account exists
    const toAccountInfo = await connection.getAccountInfo(toTokenAccount);

    // If receiver's token account doesn't exist, create it
    if (!toAccountInfo) {
        const createAccountInstruction = createAssociatedTokenAccountInstruction(
            fromPublicKey, // payer
            toTokenAccount, // associatedToken
            toPublicKey, // owner
            mintPublicKey // mint
        );
        transferInstructions.push(createAccountInstruction);
    }

    // Create the transfer instruction
    const transferInstruction = createTransferInstruction(
        fromTokenAccount, // source
        toTokenAccount, // destination
        fromPublicKey, // owner of source account
        rawAmount // amount in raw units
    );

    transferInstructions.push(transferInstruction);

    const mintInstructions = mintToCollectionV1(umi, {
        leafOwner: phantomAddressPublicKey,
        merkleTree,
        collectionMint,
        metadata: {
            name: nftName,
            uri: metadataUri,
            sellerFeeBasisPoints: 0,
            symbol: 'PILL',
            collection: { key: collectionMint, verified: false },
            creators: [
                {
                    address: treeCreatorKeypair.publicKey,
                    verified: false,
                    share: 100,
                },
            ],
        },
        treeCreatorOrDelegate: treeCreatorKeypairSigner,
        collectionAuthority: treeCreatorKeypairSigner,
    })
        .getInstructions()
        .map(toWeb3JsInstruction);

    const blockhash = (await connection.getLatestBlockhash()).blockhash;

    const message = new TransactionMessage({
        payerKey: wallet.publicKey,
        recentBlockhash: blockhash,
        instructions: [...transferInstructions, ...mintInstructions],
    }).compileToV0Message();

    const transaction = new VersionedTransaction(message);

    transaction.sign([Keypair.fromSecretKey(treeCreatorKeypair.secretKey)]);

    return transaction;
};

export const getMintPriceForSolana = async () => {
    const response = await fetch(COINGECKO_URL);

    const data = await response.json();
    const price = data?.solana?.usd;

    // $5 worth in SOL
    return Number((MINTPRICE_USD / price).toFixed(9));
};
export const getSPLTokenBalance = async (walletAddress, tokenMintAddress) => {
    const connection = new Connection(RPC_ENDPOINT_L);
    try {
        const walletPublicKey = new PublicKey(walletAddress);
        const tokenMintPublicKey = new PublicKey(tokenMintAddress);

        // Get the associated token account address
        const associatedTokenAddress = await getAssociatedTokenAddress(tokenMintPublicKey, walletPublicKey);

        // Get account info
        const accountInfo = await getAccount(connection, associatedTokenAddress);

        // Get mint info to get decimals
        const mintInfo = await connection.getParsedAccountInfo(tokenMintPublicKey);
        const decimals = mintInfo.value?.data?.parsed?.info?.decimals || 0;

        // Calculate actual balance (raw balance / 10^decimals)
        const balance = Number(accountInfo.amount) / Math.pow(10, decimals);

        return balance;
    } catch (error) {
        if (error?.toString()?.includes('TokenAccountNotFoundError')) {
            return 0;
        }
        console.error('Error fetching SPL token balance:', error?.toString());
        throw error;
    }
};

// Use Phantom’s EVM provider
const getPhantomEvmProvider = () => {
    if (typeof window !== 'undefined' && window?.ethereum?.isPhantom) {
        return window.ethereum;
    }
    throw new Error('Phantom EVM provider not found. Make sure Phantom is installed and selected.');
};
